import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';

const ViewTechnicianConsumablesModal = ({ isOpen, onClose, technicianId, technicianName }) => {
  const [consumables, setConsumables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    if (isOpen && technicianId) {
      loadTechnicianConsumables();
    }
  }, [isOpen, technicianId]);

  const loadTechnicianConsumables = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('consumables_techniciens')
        ?.select(`
          id,
          quantity,
          consumable,
          consumables:consumable (
           *
          )
        `)
        ?.eq('technicien', technicianId)
        ?.order('consumables(name)');

      if (error) throw error;
      setConsumables(data || []);
    } catch (err) {
      console.error('Error loading technician consumables:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (id, newQuantity, consumableId, oldQuantity) => {
    if (newQuantity < 0) return;

    // Vérification avec le stock disponible dans l'état local
    const currentItem = consumables.find(c => c.id === id);
    const availableStock = currentItem?.consumables?.current_stock || 0;
    const quantityDiff = newQuantity - oldQuantity;
    
    // Vérifier si on essaie d'ajouter plus que le stock disponible
    if (quantityDiff > 0 && quantityDiff > availableStock) {
      alert(`Stock insuffisant dans le stock principal ! Disponible: ${availableStock}`);
      return;
    }

    setUpdatingId(id);
    try {
      // Calculer la différence de quantité
      const quantityDiff = newQuantity - oldQuantity;
      
      // D'abord, récupérer le stock actuel depuis la BD pour être sûr
      const { data: consumable, error: fetchError } = await supabase
        ?.from('consumables')
        ?.select('current_stock')
        ?.eq('id', consumableId)
        ?.single();

      if (fetchError) throw fetchError;

      // Vérification supplémentaire avec les données fraîches de la BD
      if (quantityDiff > 0 && quantityDiff > (consumable?.current_stock || 0)) {
        alert(`Stock insuffisant dans le stock principal ! Disponible: ${consumable?.current_stock || 0}`);
        setUpdatingId(null);
        return;
      }

      // Mettre à jour la quantité du technicien
      const { error: techError } = await supabase
        ?.from('consumables_techniciens')
        ?.update({ quantity: newQuantity })
        ?.eq('id', id);

      if (techError) throw techError;

      // Mettre à jour le stock principal
      if (quantityDiff !== 0) {
        const newStock = (consumable?.current_stock || 0) - quantityDiff;
        
        const { error: stockError } = await supabase
          ?.from('consumables')
          ?.update({ current_stock: newStock })
          ?.eq('id', consumableId);

        if (stockError) throw stockError;

        // Mettre à jour l'état local avec le nouveau stock
        setConsumables(consumables.map(c => 
          c.id === id ? { 
            ...c, 
            quantity: newQuantity,
            consumables: {
              ...c.consumables,
              current_stock: newStock
            }
          } : c
        ));
      } else {
        // Si pas de changement de quantité, juste mettre à jour la quantité du technicien
        setConsumables(consumables.map(c => 
          c.id === id ? { ...c, quantity: newQuantity } : c
        ));
      }
      
    } catch (err) {
      console.error('Error updating quantity:', err);
      alert('Erreur lors de la mise à jour de la quantité');
    } finally {
      setUpdatingId(null);
    }
  };

  const deleteConsumable = async (id, consumableId, quantity) => {
    if (!window.confirm('Voulez-vous vraiment retirer ce consommable ?')) return;

    try {
      // D'abord, récupérer le stock actuel
      const { data: consumable, error: fetchError } = await supabase
        ?.from('consumables')
        ?.select('current_stock')
        ?.eq('id', consumableId)
        ?.single();

      if (fetchError) throw fetchError;

      // Supprimer l'entrée du technicien
      const { error: deleteError } = await supabase
        ?.from('consumables_techniciens')
        ?.delete()
        ?.eq('id', id);

      if (deleteError) throw deleteError;

      // Remettre la quantité dans le stock principal
      const newStock = (consumable?.current_stock || 0) + quantity;
      
      const { error: stockError } = await supabase
        ?.from('consumables')
        ?.update({ current_stock: newStock })
        ?.eq('id', consumableId);

      if (stockError) throw stockError;

      setConsumables(consumables.filter(c => c.id !== id));
    } catch (err) {
      console.error('Error deleting consumable:', err);
      alert('Erreur lors de la suppression');
    }
  };

  const handleIncrement = (item) => {
    const availableStock = item.consumables?.current_stock || 0;
    if (availableStock <= 0) {
      alert('Stock principal épuisé ! Impossible d\'augmenter la quantité.');
      return;
    }
    updateQuantity(
      item.id, 
      item.quantity + 1, 
      item.consumable, 
      item.quantity
    );
  };

  const handleDecrement = (item) => {
    if (item.quantity <= 0) return;
    updateQuantity(
      item.id, 
      item.quantity - 1, 
      item.consumable, 
      item.quantity
    );
  };

  const handleInputChange = (item, value) => {
    const newQuantity = parseInt(value) || 0;
    if (newQuantity === item.quantity) return; // Éviter les mises à jour inutiles
    
    // Vérification préalable
    const availableStock = item.consumables?.current_stock || 0;
    const quantityDiff = newQuantity - item.quantity;
    
    if (quantityDiff > 0 && quantityDiff > availableStock) {
      alert(`Stock insuffisant dans le stock principal ! Disponible: ${availableStock}`);
      return;
    }
    
    updateQuantity(
      item.id, 
      newQuantity, 
      item.consumable, 
      item.quantity
    );
  };

  const getStockStatus = (quantity, minThreshold) => {
    if (quantity <= 0) return { color: 'text-destructive', icon: 'AlertTriangle', label: 'Rupture' };
    if (quantity <= (minThreshold || 5)) return { color: 'text-warning', icon: 'AlertCircle', label: 'Stock bas' };
    return { color: 'text-success', icon: 'CheckCircle', label: 'OK' };
  };

  const getMainStockStatus = (currentStock, minThreshold) => {
    if (currentStock <= 0) return { color: 'text-destructive', bg: 'bg-destructive/10', label: 'Rupture' };
    if (currentStock <= (minThreshold || 5)) return { color: 'text-warning', bg: 'bg-warning/10', label: 'Stock bas' };
    return { color: 'text-success', bg: 'bg-success/10', label: 'OK' };
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1020] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="bg-card rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
        <div className="p-6 border-b border-border sticky top-0 bg-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="Package" size={24} className="text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Stock de consommables</h3>
                <p className="text-sm text-muted-foreground">{technicianName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : consumables.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="Package" size={48} className="mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground mb-4">Aucun consommable assigné à ce technicien</p>
              <Button variant="outline" onClick={onClose}>
                Fermer
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* En-tête du tableau */}
              <div className="grid grid-cols-12 gap-4 px-4 py-2 bg-muted/50 rounded-lg text-sm font-medium text-muted-foreground">
                <div className="col-span-3">Consommable</div>
                <div className="col-span-1">Unité</div>
                <div className="col-span-2">Stock Principal</div>
                <div className="col-span-2">Quantité Tech.</div>
                <div className="col-span-2">Actions</div>
              </div>

              {/* Liste des consommables */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {consumables.map((item) => {
                  const consumable = item.consumables;
                  const status = getStockStatus(item.quantity, consumable?.minimum_threshold);
                  const mainStockStatus = getMainStockStatus(consumable?.current_stock || 0, consumable?.minimum_threshold);
                  
                  return (
                    <div key={item.id} className="grid grid-cols-12 gap-4 px-4 py-3 bg-card border border-border rounded-lg items-center">
                      <div className="col-span-3">
                        <div className="font-medium">{consumable?.name}</div>
                        {consumable?.minimum_threshold && (
                          <div className="text-xs text-muted-foreground">
                            Seuil: {consumable.minimum_threshold}
                          </div>
                        )}
                      </div>
                      <div className="col-span-1 text-sm">{consumable?.unit}</div>
                      <div className="col-span-2">
                        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${mainStockStatus.bg}`}>
                          <span className={`font-semibold ${mainStockStatus.color}`}>
                            {consumable?.current_stock || 0}
                          </span>
                          <span className="text-muted-foreground">({mainStockStatus.label})</span>
                        </div>
                      </div>
                      <div className="col-span-2">
                        <div className="flex items-center gap-1">
                          <Icon name={status.icon} size={14} className={status.color} />
                          <span className={`font-semibold ${status.color}`}>
                            {item.quantity}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {status.label}
                        </div>
                      </div>
                      <div className="col-span-2 flex gap-1">
                        <Button
                          variant="ghost"
                          size="xs"
                          iconName="Minus"
                          onClick={() => handleDecrement(item)}
                          disabled={updatingId === item.id || item.quantity <= 0}
                        />
                        <Input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleInputChange(item, e.target.value)}
                          className="w-16 text-center"
                          min="0"
                          disabled={updatingId === item.id}
                        />
                        <Button
                          variant="ghost"
                          size="xs"
                          iconName="Plus"
                          onClick={() => handleIncrement(item)}
                          disabled={updatingId === item.id || (item.consumables?.current_stock || 0) <= 0}
                        />
                        <Button
                          variant="ghost"
                          size="xs"
                          iconName="Trash2"
                          onClick={() => deleteConsumable(item.id, item.consumable, item.quantity)}
                          className="text-destructive hover:text-destructive/80 ml-1"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Total */}
              <div className="bg-muted/50 rounded-lg p-4 mt-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Total consommables</span>
                  <span className="text-lg font-semibold text-foreground">{consumables.length}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewTechnicianConsumablesModal;